import { OVERWERK_GRENS, periodeTotaal, postenRows, urenRows } from '../lib/periode';
import { useGeld } from '../lib/geld';
import { Link } from 'react-router-dom';
import { useMemo, useState, type ReactNode } from 'react';
import { addDays, format, parseISO } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle, Banknote, CalendarClock, CheckCircle2, Clock, Coins, FileText, Gift, MapPin, Palmtree, Wallet,
} from 'lucide-react';
import { api } from '../lib/api';
import { cn, formatDate, formatMoney, formatTime } from '../lib/utils';
import { shiftKind, KIND_LABEL, KIND_STYLE } from '../lib/shift-kind';
import { TrendWidget } from '../components/widgets';
import { ShiftDrawer } from '../components/ShiftDrawer';
import { VerlengdTag } from '../components/Verlengd';
import { Card, CardLink, EmptyState, ICON, Kpi, ListRow, PageHeader, PageSkeleton, Sheet } from '../components/ui';
import type { DashboardData } from '../types';
import type { EigenProef, UitkeringenData } from '../lib/api';


/** Opvallend verloop in de kleur van de dienst (zelfde kleuren als de kalender). */
const HERO_BG: Record<ReturnType<typeof shiftKind>, string> = {
  vroeg: 'bg-gradient-to-r from-sky-500/25 via-sky-500/10 to-transparent',
  laat: 'bg-gradient-to-r from-orange-500/25 via-orange-500/10 to-transparent',
  nacht: 'bg-gradient-to-r from-indigo-500/30 via-indigo-500/10 to-transparent',
  verlof: 'bg-gradient-to-r from-emerald-500/25 via-emerald-500/10 to-transparent',
  ziek: 'bg-gradient-to-r from-rose-500/25 via-rose-500/10 to-transparent',
  geruild: 'bg-gradient-to-r from-slate-500/20 to-transparent',
};

/** Vaste kolommen voor de daglijst: streep, dag, tijd, soort, bedrag. */
const RIJ = 'grid grid-cols-[4px_4.5rem_minmax(0,1fr)_5rem] items-center gap-3 px-4 sm:grid-cols-[4px_6rem_9rem_minmax(0,1fr)_6rem] sm:px-5';
const PIL = 'inline-flex w-[4.75rem] justify-center rounded-full py-0.5 text-xs font-medium';

const capFirst = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
const uren = (n: number) => `${n.toFixed(1).replace('.', ',').replace(',0', '')}u`;

export function DashboardPage() {
  const { modus: geldModus, label: geldLabel } = useGeld();
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: api.dashboard.get,
    refetchInterval: 15 * 60_000,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  if (error) {
    return (
      <Card>
        <EmptyState icon={AlertCircle} title="Het overzicht kon niet laden" text={(error as Error).message} />
      </Card>
    );
  }
  if (isLoading || !data) return <PageSkeleton kpis={4} />;

  const p = data.current_period;
  const netto = geldModus === 'netto';
  const lb = data.leave_balance;
  const verlof = lb ? (lb.wettelijk_saldo ?? 0) + (lb.bovenwettelijk_saldo ?? 0) + (lb.feestdagverlof_saldo ?? 0) : null;

  return (
    <div className="page">
      <PageHeader title="Overzicht" sub={capFirst(formatDate(data.today, 'EEEE d MMMM yyyy'))} />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Kpi
          icon={Coins}
          label={p ? `${geldLabel} periode ${p.period_number}` : geldLabel}
          value={p ? periodeTotaal(p.totaal, geldModus, p.projected_reiskosten) : null}
          format={formatMoney}
          rows={p ? postenRows(p.posten, geldModus, p.projected_reiskosten) : undefined}
          to="/statistieken"
        />
        <Kpi
          icon={Clock}
          label={p ? `Uren periode ${p.period_number}` : 'Uren'}
          value={p ? p.hours_gepland : null}
          format={uren}
          rows={p ? urenRows(p.soorten, p.hours_gepland, uren) : undefined}
          bar={p ? { value: p.hours_gepland, max: OVERWERK_GRENS, over: p.hours_gepland > OVERWERK_GRENS } : undefined}
          to="/kalender"
        />
        <Kpi
          icon={Wallet}
          label="Uitbetaling"
          text={p ? (p.days_until_payday === 0 ? 'Vandaag' : `${p.days_until_payday} dagen`) : '–'}
          rows={p ? uitbetalingRows(p) : undefined}
        />
        <Kpi
          icon={Palmtree}
          label="Verlofsaldo"
          value={verlof}
          format={uren}
          rows={
            lb
              ? [
                  ['Wettelijk', uren(lb.wettelijk_saldo ?? 0)],
                  ['Bovenwettelijk', uren(lb.bovenwettelijk_saldo ?? 0)],
                  lb.period_number != null && ['Bron', `urenbijlage P${lb.period_number}`],
                ]
              : undefined
          }
          to="/verlof"
        />
      </div>

      <div className="grid gap-3 sm:gap-4 lg:grid-cols-3 [&>*]:min-w-0">
        <div className="lg:col-span-2">
          <WeekCard data={data} />
        </div>
        <div className="flex flex-col gap-3 sm:gap-4">
          <Attention data={data} />
          <Uitkeringen />
        </div>
      </div>

      <TrendWidget trend={data.trend} />
    </div>
  );
}

/* ------------------------------------------------------------------ */

/** Uitbetaling: loon, uitkering en reiskosten als losse rijen, met het totaal op je rekening. */
function uitbetalingRows(p: DashboardData['current_period'] & object): Array<[string, string] | false> {
  if (p.projected_netto == null) return [['Betaaldag', formatDate(p.payday)]];
  const u = p.uitkering;
  const totaal = p.op_rekening ?? p.projected_netto + (u?.netto ?? 0) + p.projected_reiskosten;
  return [
    ['Betaaldag', formatDate(p.payday)],
    ['Loon', formatMoney(p.projected_netto)],
    !!u && u.netto != null && [u.soort === 'eindejaarsuitkering' ? 'Uitkering' : 'Vakantiegeld', formatMoney(u.netto)],
    p.projected_reiskosten > 0 && ['Reiskosten', formatMoney(p.projected_reiskosten)],
    ['Totaal', `± ${formatMoney(totaal)}`],
  ];
}

/**
 * Komende 7 dagen in één kaart: bovenin de dienst die nu bezig is of als eerste komt,
 * daaronder elke dag (ook vrije dagen), zodat je week in één oogopslag te zien is.
 */
function WeekCard({ data }: { data: DashboardData }) {
  const { geld, label } = useGeld();
  // Dienst openen in dezelfde modal als de kalender, zonder van pagina te wisselen.
  const [openId, setOpenId] = useState<number | null>(null);
  const now = new Date();
  const lopend = data.today_shifts.find((s) => {
    const end = new Date(`${s.shift_date}T${s.end_time}:00`);
    if (s.crosses_midnight) end.setDate(end.getDate() + 1);
    return end > now;
  });
  const s = lopend ?? data.next_shift;

  const dagen = useMemo(() => {
    const sorted = [...data.week_shifts].sort((a, b) => (a.shift_date + a.start_time).localeCompare(b.shift_date + b.start_time));
    return Array.from({ length: 7 }, (_, i) => {
      const d = format(addDays(parseISO(data.today), i), 'yyyy-MM-dd');
      return { date: d, shifts: sorted.filter((x) => x.shift_date === d) };
    });
  }, [data.week_shifts, data.today]);

  const week = useMemo(() => {
    const alle = dagen.flatMap((d) => d.shifts);
    return {
      diensten: alle.length,
      uren: alle.reduce((sum, x) => sum + (x.total_hours ?? 0), 0),
      bruto: alle.reduce((sum, x) => sum + (x.total_gross ?? 0), 0),
    };
  }, [dagen]);

  let hero: ReactNode = (
    <div className="flex items-center gap-2 px-4 py-4 text-sm text-slate-400 sm:px-5">
      <CalendarClock className={ICON.sm} aria-hidden /> Geen dienst gepland. Nieuwe diensten komen vanzelf uit je rooster.
    </div>
  );
  if (s) {
    const start = new Date(`${s.shift_date}T${s.start_time}:00`);
    const end = new Date(`${s.shift_date}T${s.end_time}:00`);
    if (end <= start) end.setDate(end.getDate() + 1);
    const active = now >= start && now <= end;
    const kind = shiftKind(s);
    const mins = Math.round((start.getTime() - now.getTime()) / 60000);
    const when = active ? 'Nu bezig' : mins < 60 * 24 ? `Over ${Math.floor(mins / 60)}u ${mins % 60}m` : `Over ${Math.round(mins / 1440)} dagen`;
    hero = (
      <button
        type="button"
        onClick={() => setOpenId(s.id)}
        className={cn(
          'flex w-full flex-col gap-3 border-b border-l-[3px] text-left border-b-surface-line px-4 py-4 transition-[filter] duration-fast ease-calm hover:brightness-125 sm:flex-row sm:items-center sm:justify-between sm:px-5',
          KIND_STYLE[kind].bar,
          HERO_BG[kind]
        )}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="t-label">{s.shift_date === data.today ? 'Vandaag' : capFirst(formatDate(s.shift_date, 'EEEE d MMMM'))}</span>
            <span className={active ? 'status-ok' : 'status-accent'}>{when}</span>
          </div>
          <div className="mt-1.5 flex items-center gap-3">
            <span className="t-kpi text-slate-50">
              {formatTime(s.start_time)}–{formatTime(s.end_time)}
            </span>
            <VerlengdTag shift={s} className="text-sm" />
            <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ring-white/10', KIND_STYLE[kind].text)}>
              <span className={cn('h-1.5 w-1.5 rounded-full', KIND_STYLE[kind].dot)} aria-hidden />
              {KIND_LABEL[kind]}
            </span>
          </div>
          {s.object_naam && (
            <div className="mt-1.5 flex min-w-0 items-center gap-1.5 text-sm text-slate-300">
              <MapPin className={cn(ICON.sm, 'shrink-0')} aria-hidden />
              <span className="truncate">{s.object_naam}</span>
            </div>
          )}
        </div>
        <dl className="flex gap-8 sm:text-right">
          <div>
            <dt className="t-label">Uren</dt>
            <dd className="t-num mt-1 text-base font-semibold text-slate-100">{s.total_hours != null ? uren(s.total_hours) : '–'}</dd>
          </div>
          <div>
            <dt className="t-label">{label}</dt>
            <dd className="t-num mt-1 text-base font-semibold text-slate-100">{s.total_gross != null ? geld(s.total_gross, s.shift_date) : '–'}</dd>
          </div>
        </dl>
      </button>
    );
  }

  return (
    <Card flush title="Komende 7 dagen" icon={CalendarClock} action={<CardLink to="/kalender">Kalender</CardLink>} className="flex h-full flex-col">
      <div className="border-t border-surface-line">{hero}</div>
      <div className="flex flex-1 flex-col">
        <div role="table" aria-label="Komende 7 dagen">
          <div role="row" className={cn(RIJ, 'border-b border-surface-line py-2 text-xs text-slate-500')}>
            <span aria-hidden />
            <span role="columnheader">Dag</span>
            <span role="columnheader">Tijd</span>
            <span role="columnheader" className="hidden sm:block">Soort</span>
            <span role="columnheader" className="text-right">
              {label}
            </span>
          </div>
          <div className="divide-y divide-surface-line/60">
            {dagen.flatMap(({ date, shifts }, i) => {
              const vandaag = date === data.today;
              const dag = (
                <span role="cell" className={cn('truncate text-sm', vandaag ? 'font-medium text-brand-300' : shifts.length ? 'text-slate-200' : 'text-slate-500')}>
                  {vandaag ? 'Vandaag' : formatDate(date, 'EEEEEE d MMM')}
                </span>
              );
              if (!shifts.length) {
                return [
                  <div key={date} role="row" className={cn(RIJ, 'enter py-2.5')} style={{ '--i': i } as React.CSSProperties}>
                    <span className="h-6 w-1 rounded-full bg-emerald-400/40" aria-hidden />
                    {dag}
                    <span role="cell" className="text-sm">
                      <span className="text-emerald-200/70 sm:hidden">Vrij</span>
                      <span className="hidden text-slate-600 sm:inline">—</span>
                    </span>
                    <span role="cell" className="hidden sm:block">
                      <span className={cn(PIL, 'bg-emerald-400/10 text-emerald-200/80')}>Vrij</span>
                    </span>
                    <span role="cell" className="text-right text-sm text-slate-600">
                      —
                    </span>
                  </div>,
                ];
              }
              return shifts.map((x) => {
                const kind = shiftKind(x);
                return (
                  <button
                    key={x.id}
                    type="button"
                    role="row"
                    onClick={() => setOpenId(x.id)}
                    className={cn(RIJ, 'enter w-full py-2.5 text-left transition-colors duration-fast ease-calm hover:bg-white/[0.03]')}
                    style={{ '--i': i } as React.CSSProperties}
                  >
                    <span className={cn('h-6 w-1 rounded-full', KIND_STYLE[kind].dot)} aria-hidden />
                    {dag}
                    <span role="cell" className="t-num text-sm font-semibold text-slate-50">
                      {formatTime(x.start_time)}–{formatTime(x.end_time)}
                      <VerlengdTag shift={x} className="ml-1.5" />
                    </span>
                    <span role="cell" className="hidden sm:block">
                      <span className={cn(PIL, KIND_STYLE[kind].bg, KIND_STYLE[kind].text)}>{KIND_LABEL[kind]}</span>
                    </span>
                    <span role="cell" className="t-num text-right text-sm text-slate-200">
                      {x.total_gross != null ? geld(x.total_gross, x.shift_date) : '—'}
                    </span>
                  </button>
                );
              });
            })}
          </div>
        </div>
        {week.diensten > 0 && (
          <dl className="mt-auto grid grid-cols-3 border-t border-surface-line bg-white/[0.015]">
            <div className="px-4 py-3 sm:px-5">
              <dt className="t-label">Diensten</dt>
              <dd className="t-num mt-0.5 text-sm font-semibold text-slate-100">{week.diensten}</dd>
            </div>
            <div className="px-4 py-3 sm:px-5">
              <dt className="t-label">Uren</dt>
              <dd className="t-num mt-0.5 text-sm font-semibold text-slate-100">{uren(week.uren)}</dd>
            </div>
            <div className="px-4 py-3 text-right sm:px-5">
              <dt className="t-label">Totaal {label.toLowerCase()}</dt>
              <dd className="t-num mt-0.5 text-sm font-semibold text-money">{geld(week.bruto, data.today)}</dd>
            </div>
          </dl>
        )}
      </div>
      <ShiftDrawer open={openId != null} shiftId={openId} initialDate={null} onClose={() => setOpenId(null)} />
    </Card>
  );
}

function Attention({ data }: { data: DashboardData }) {
  const items = [
    ...data.alerts
      .filter((a) => a.priority === 'high' || a.priority === 'medium')
      .map((a) => ({ id: `a${a.id}`, title: a.title, body: a.body, link: a.link ?? '/afwijkingen', high: a.priority === 'high' })),
    ...data.expiring_certs.map((c) => ({
      id: `c${c.id}`,
      title: `${c.naam} verloopt`,
      body: `Geldig tot ${formatDate(c.geldig_tot)}`,
      link: '/instellingen',
      high: true,
    })),
  ];
  if (!items.length) {
    return (
      <div className="card enter flex items-center gap-2 text-sm text-slate-300">
        <CheckCircle2 className={cn(ICON.sm, 'text-money')} aria-hidden /> Niets dat aandacht nodig heeft
      </div>
    );
  }
  return (
    <Card flush title="Aandacht nodig" icon={AlertCircle} action={<CardLink to="/afwijkingen">Alles</CardLink>}>
      <div className="list border-t border-surface-line">
        {items.slice(0, 4).map((it, i) => (
          <ListRow
            key={it.id}
            i={i}
            icon={AlertCircle}
            iconClass={it.high ? 'text-red-300' : 'text-amber-200'}
            title={it.title}
            meta={it.body ?? undefined}
            to={it.link}
          />
        ))}
      </div>
    </Card>
  );
}

/** Komende eindejaarsuitkering en vakantiegeld; de AFAS-proefberekening opent in een paneel. */
function Uitkeringen() {
  const { modus: geldModus } = useGeld();
  const { data } = useQuery({ queryKey: ['uitkeringen'], queryFn: api.uitkeringen.get, staleTime: 30 * 60_000 });
  const { data: eigen } = useQuery({ queryKey: ['eigen-proef'], queryFn: () => api.dashboard.proef(), staleTime: 15 * 60_000 });
  const [proefOpen, setProefOpen] = useState(false);
  if (!data) return null;
  const items = [data.eindejaarsuitkering, data.vakantiegeld].sort((a, b) => (a.betaaldag ?? '9').localeCompare(b.betaaldag ?? '9'));
  return (
    <Card flush title="Komende uitkeringen" icon={Gift} className="flex-1">
      <div className="list border-t border-surface-line">
        {items.map((u, i) => (
          <ListRow
            key={u.soort}
            i={i}
            icon={u.soort === 'eindejaarsuitkering' ? Gift : Banknote}
            iconClass="text-money"
            title={`${u.soort === 'eindejaarsuitkering' ? 'Eindejaarsuitkering' : 'Vakantiegeld'} ${u.year}`}
            meta={`${u.betaaldag ? formatDate(u.betaaldag) : 'Datum onbekend'}${u.bron === 'schatting' ? ', schatting' : ''}`}
            value={(geldModus === 'netto' ? u.netto : u.bedrag) != null ? formatMoney((geldModus === 'netto' ? u.netto : u.bedrag)!) : '–'}
            valueClass="font-medium text-money"
            to="/kalender"
          />
        ))}
        {eigen && (
          <ListRow
            i={2}
            icon={FileText}
            title={`Proefberekening periode ${eigen.period}`}
            meta="Met je echte diensten"
            value={formatMoney(eigen.uit_te_betalen)}
            onClick={() => setProefOpen(true)}
          />
        )}
      </div>
      {eigen && <ProefSheet eigen={eigen} afas={data.proefberekening} open={proefOpen} onClose={() => setProefOpen(false)} />}
    </Card>
  );
}

/** Proefberekening als loonstrook: regels uit je diensten, inhoudingen, wat er op je rekening komt. */
function ProefSheet({
  eigen,
  afas,
  open,
  onClose,
}: {
  eigen: EigenProef;
  afas: UitkeringenData['proefberekening'];
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={`Proefberekening periode ${eigen.period}`}
      sub={`${formatDate(eigen.start_date)} t/m ${formatDate(eigen.end_date)}, uitbetaling ${formatDate(eigen.payday)}`}
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-surface-line p-3">
          <div className="t-label">Bruto</div>
          <div className="t-num mt-1 text-xl font-semibold">{formatMoney(eigen.bruto)}</div>
        </div>
        <div className="rounded-lg border border-surface-line p-3">
          <div className="t-label">Op je rekening</div>
          <div className="t-num mt-1 text-xl font-semibold text-money">{formatMoney(eigen.uit_te_betalen)}</div>
        </div>
      </div>
      <h3 className="t-section mt-5">Per soort</h3>
      <p className="t-muted mt-1">Zelfde verdeling als op het overzicht. Netto per soort: bruto min loonheffing en premies van deze periode; de uitkering met het bijzonder tarief.</p>
      <table className="table mt-2">
        <thead>
          <tr>
            <th className="!px-0">Soort</th>
            <th className="r !px-0">Bruto</th>
            <th className="r !px-0">Netto</th>
          </tr>
        </thead>
        <tbody>
          {eigen.posten.map((p) => (
            <tr key={p.key}>
              <td className="!px-0 text-slate-300">{p.label}</td>
              <td className="r !px-0">{formatMoney(p.bruto)}</td>
              <td className="r !px-0">{p.netto != null ? formatMoney(p.netto) : '–'}</td>
            </tr>
          ))}
          <tr>
            <td className="!px-0 font-medium text-slate-100">Totaal periode</td>
            <td className="r !px-0 font-medium text-slate-100">{formatMoney(eigen.totaal.bruto)}</td>
            <td className="r !px-0 font-medium text-slate-100">{eigen.totaal.netto != null ? formatMoney(eigen.totaal.netto) : '–'}</td>
          </tr>
        </tbody>
      </table>

      <h3 className="t-section mt-6">Zoals op je loonstrook</h3>
      <table className="table mt-2">
        <tbody>
          {eigen.regels.map((r, i) => (
            <tr key={`r${i}`}>
              <td className="!px-0 text-slate-300">
                {r.omschrijving}
                {r.aantal ? <span className="text-slate-500"> ({r.aantal})</span> : null}
              </td>
              <td className="r !px-0">{formatMoney(r.bedrag)}</td>
            </tr>
          ))}
          <tr>
            <td className="!px-0 font-medium text-slate-100">Bruto</td>
            <td className="r !px-0 font-medium text-slate-100">{formatMoney(eigen.bruto)}</td>
          </tr>
          {eigen.inhoudingen.map((r, i) => (
            <tr key={`i${i}`}>
              <td className="!px-0 text-slate-400">{r.omschrijving}</td>
              <td className="r !px-0 text-slate-400">{formatMoney(r.bedrag)}</td>
            </tr>
          ))}
          <tr>
            <td className="!px-0 font-medium text-slate-100">Netto</td>
            <td className="r !px-0 font-medium text-slate-100">{formatMoney(eigen.netto)}</td>
          </tr>
          <tr>
            <td className="!px-0 text-slate-300">Reiskosten (onbelast)</td>
            <td className="r !px-0">{formatMoney(eigen.reiskosten)}</td>
          </tr>
          <tr>
            <td className="!px-0 font-semibold text-slate-50">Op je rekening</td>
            <td className="r !px-0 font-semibold text-money">{formatMoney(eigen.uit_te_betalen)}</td>
          </tr>
        </tbody>
      </table>
      <p className="mt-4 text-sm text-slate-400">
        Berekend met je diensten in deze periode, inclusief wat nog ingeroosterd staat. Getoetst aan P9 en P10: minder dan € 1 verschil
        met de loonstrook.
        {afas?.uit_te_betalen != null && afas.period === eigen.period && (
          <> AFAS rekent tot de verwerking alleen met je contracturen en komt op {formatMoney(afas.uit_te_betalen)}.</>
        )}
      </p>
    </Sheet>
  );
}
